import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import type { Tone } from './Status';

/**
 * Pigułka statusu (lifting 17.10.2026, DECISIONS „Lekki lifting UI”, DESIGN „Lifting 17.10.2026”): lekkie tło
 * w barwie znaczenia, w środku kropka albo ikona i etykieta. Zastępuje samą kropkę (`Status`) tam, gdzie status
 * trzeba szybko znaleźć wzrokiem (listy, karty, nagłówki). Kolor niesie znaczenie, nie dekoruje:
 *
 *   neutral  — nic się nie dzieje (Do zrobienia, szkic)            → szare tło `soft`
 *   progress — w toku, AI pracuje                                  → akcent
 *   waiting  — czeka, zablokowane, wstrzymane                      → bursztyn
 *   review   — do sprawdzenia / do odbioru                         → fiolet
 *   done     — gotowe, opłacone, działa                            → zieleń
 *   danger   — po terminie, błąd, alarm                            → czerwień
 *
 * Przyjmuje też tony `Status` (`accent` = progress, `success` = done, `warning` = waiting), żeby moduły mogły
 * przejść z kropki na pigułkę bez nowego słownika.
 */
export type PillTone = 'neutral' | 'progress' | 'waiting' | 'review' | 'done' | 'danger';

const ALIAS: Record<Tone, PillTone> = {
  neutral: 'neutral',
  accent: 'progress',
  success: 'done',
  warning: 'waiting',
  danger: 'danger',
};

export function pillTone(tone: PillTone | Tone): PillTone {
  return tone in ALIAS ? ALIAS[tone as Tone] : (tone as PillTone);
}

export const PILL_CLASSES: Record<PillTone, { pill: string; dot: string }> = {
  neutral: { pill: 'bg-soft text-fg-secondary', dot: 'bg-fg-muted' },
  progress: { pill: 'bg-accent-subtle text-accent', dot: 'bg-accent' },
  waiting: { pill: 'bg-warning-subtle text-warning', dot: 'bg-warning' },
  review: { pill: 'bg-review-subtle text-review', dot: 'bg-review' },
  done: { pill: 'bg-success-subtle text-success', dot: 'bg-success' },
  danger: { pill: 'bg-danger-subtle text-danger', dot: 'bg-danger' },
};

export function StatusPill({
  tone = 'neutral',
  icon: Icon,
  size = 'm',
  children,
  className,
  title,
}: {
  tone?: PillTone | Tone;
  /** ikona zamiast kropki (np. Sparkles przy stanie AI, Check przy „Gotowe”) */
  icon?: LucideIcon;
  /** m = 22 px (listy, karty), s = 20 px (gęste miejsca) */
  size?: 's' | 'm';
  children: ReactNode;
  className?: string;
  title?: string;
}) {
  const t = PILL_CLASSES[pillTone(tone)];
  return (
    <span
      data-tone={pillTone(tone)}
      title={title}
      className={cn(
        'inline-flex max-w-full shrink-0 items-center gap-1.5 rounded-full font-medium whitespace-nowrap',
        size === 'm' ? 'h-[22px] px-2 text-caption' : 'h-5 px-1.5 text-micro',
        t.pill,
        className,
      )}
    >
      {Icon ? (
        <Icon size={size === 'm' ? 12 : 11} strokeWidth={2} aria-hidden className="shrink-0" />
      ) : (
        <span aria-hidden className={cn('inline-block size-1.5 shrink-0 rounded-full', t.dot)} />
      )}
      <span className="min-w-0 truncate">{children}</span>
    </span>
  );
}
